import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { isServiceWithVariedPrice } from '@wix/bookings-calendar-catalog-viewer-mapper';
import { useSettings } from '@wix/tpa-settings/react';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import React, { FC, memo, useCallback } from 'react';
import { Divider as TPADivider } from 'wix-ui-tpa/cssVars';
import { EnrichedService } from '../../../../../../types/types';
import { ReferralInfo } from '../../../../../../utils/bi/consts';
import {
  ClickDestination,
  getBookButtonClickDestination,
  getServiceNameClickDestination,
} from '../../../../../../utils/getClickDestination/getClickDestination';
import { generateServiceInfoDetails } from '../../../../../../utils/serviceDetails/displayOptions';
import { getStaffMembersDisplayText } from '../../../../../../utils/serviceDetails/serviceDetails';
import settingsParams from '../../../../settingsParams';
import { useAddVisibilityClass } from '../../../../hooks/useAddVisibilityClass';
import { useVisibilityCheck } from '../../../../hooks/useVisibilityCheck';
import { useServiceInfoLayout } from '../../../hooks/useServiceInfoLayout';
import { useWidgetActions } from '../../../hooks/useWidgetActions';
import { useWidgetViewModel } from '../../../hooks/useWidgetViewModel';
import { SERVICE_INFO_LABEL_ID } from '../../consts';
import {
  BookButton as BookButtonComponent,
  BookButtonProps,
} from './BookButton/BookButton';
import {
  DataHooks,
  ServiceDetailsType,
  ServiceInfoLayoutOption,
} from './consts';
import { CourseAvailability } from './CourseAvailability/CourseAvailability';
import { Details } from './Details/Details';
import { classes as detailsClasses } from './Details/Details.st.css';
import { DiscountName } from './DiscountName/DiscountName';
import { ExplorePlans } from './ExplorePlans/ExplorePlans';
import { MoreInfoButton as MoreInfoButtonComponent } from './MoreInfoButton/MoreInfoButton';
import { OnlineBadge as OnlineBadgeComponent } from './OnlineBadge/OnlineBadge';
import { classes } from './ServiceInfo.st.css';
import { TagLine } from './TagLine/TagLine';
import { Title } from './Title/Title';

export type ServiceInfoProps = {
  service: EnrichedService;
  statusInfo?: React.ReactNode;
  availability?: React.ReactNode;
  hrefUrl?: string;
  shouldNavigateImmediately: boolean;
};

export const ServiceInfo: FC<ServiceInfoProps> = ({
  service,
  shouldNavigateImmediately,
  hrefUrl,
}) => {
  const serviceInfoLayout = useServiceInfoLayout();
  const { businessInfo, coursesAvailability, ctaLoadingServiceId } =
    useWidgetViewModel();
  const { language } = useEnvironment();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { shouldBeVisible } = useVisibilityCheck();
  const { onServiceClick, setCtaLoadingService } = useWidgetActions();
  const settings = useSettings();

  const isTagLineVisible = shouldBeVisible('isTagLineVisible');
  const isServiceOfferedDaysVisible = shouldBeVisible(
    'isServiceOfferedDaysVisible',
  );
  const isServiceStartDateVisible = shouldBeVisible(
    'isServiceStartDateVisible',
  );
  const isServiceDurationVisible = shouldBeVisible('isServiceDurationVisible');
  const isServicePriceVisible = shouldBeVisible('isServicePriceVisible');
  const isStaffMemberVisible = shouldBeVisible('isStaffMemberVisible');

  const staffMembersText = isStaffMemberVisible
    ? getStaffMembersDisplayText({
        staffMembers: service.staffMemberDetails?.staffMembers,
        showAllStaffMembersNames: settings.get(
          settingsParams.showAllStaffMembersNames,
        ),
        multipleStaffMembersText:
          settings.get(settingsParams.multipleStaffMembersText) ||
          settings.getDefaultValue(settingsParams.multipleStaffMembersText),
      })
    : undefined;

  const displayOptions = generateServiceInfoDetails({
    service,
    displayOptions: {
      isServiceOfferedDaysVisible,
      isServiceStartDateVisible,
      isServicePriceVisible,
      isServiceDurationVisible,
      isTagLineVisible,
    },
    businessInfo,
    language,
    t,
    coursesAvailability,
    isAllDayMultiDayEnabled: experiments.enabled(
      'specs.bookings.AllDayMultiDayEvents',
    ),
  });

  const {
    tagLine,
    offeredDays,
    duration,
    price,
    isPassedEndDate,
    isTooLateToBook,
    numberOfSpotsLeft,
    startDate,
  } = displayOptions;

  const titleTabIndex = useTitleTabIndex(service, numberOfSpotsLeft);
  const isCourseType = service.type === ServiceType.COURSE;
  const courseStatus = isServiceStartDateVisible ? (
    startDate ? (
      <StartDate
        startDate={startDate}
        locale={businessInfo.language}
        ellipsis={serviceInfoLayout === ServiceInfoLayoutOption.HORIZONTAL}
      />
    ) : (
      <Ended isEnded={!!isPassedEndDate} />
    )
  ) : null;

  return (
    <div
      data-id={service.id}
      data-hook={DataHooks.ROOT}
      aria-labelledby={SERVICE_INFO_LABEL_ID}
      role="group"
      className={classes.root}
    >
      <div className={classes.details}>
        <div className={classes.titleArea}>
          <Title
            tabIndex={titleTabIndex}
            ariaDescribedBy={tagLine ? `${service.id}-tagLine` : undefined}
            title={service.name!}
            url={hrefUrl}
            id={service.id!}
            onClick={() => {
              if (shouldNavigateImmediately) {
                setCtaLoadingService(service.id!);
              }
              void onServiceClick({
                service,
                referralInfo: ReferralInfo.SERVICE_TITLE,
              });
            }}
          />
          <OnlineBadge isOfferedOnline={service.conferencing?.enabled!} />
          {tagLine ? (
            <TagLine id={`${service.id}-tagLine`} tagLine={tagLine} />
          ) : null}
          {staffMembersText ? (
            <StaffMember
              text={staffMembersText}
              ellipsis={serviceInfoLayout === ServiceInfoLayoutOption.HORIZONTAL}
            />
          ) : null}
          <MoreInfoButton service={service} />
          <Divider />
        </div>
        <div className={classes.priceArea}>
          {offeredDays ? <OfferedDays offeredDays={offeredDays} /> : null}
          {courseStatus}
          {duration ? <Duration duration={duration} /> : null}
          {price?.displayedPrice ? (
            <Price
              displayedPrice={price.displayedPrice}
              srOnlyPrice={price.srOnlyPrice!}
              priceAfterDiscount={price.priceAfterDiscount}
              discountName={price.discountName}
              isVariedPrice={isServiceWithVariedPrice(service)}
            />
          ) : null}
          {isCourseType && (
            <CourseAvailability
              numberOfSpotsLeft={numberOfSpotsLeft}
              checkDivider={false}
              isTooLateToBook={!!isTooLateToBook}
              isPassedEndDate={!!isPassedEndDate}
              className={classes.HorizontalCourseAvailability}
            />
          )}
        </div>
        {isCourseType && (
          <CourseAvailability
            numberOfSpotsLeft={numberOfSpotsLeft}
            checkDivider={true}
            isTooLateToBook={!!isTooLateToBook}
            isPassedEndDate={!!isPassedEndDate}
            className={classes.VerticalCourseAvailability}
          />
        )}
      </div>
      <ServiceActions
        isTooLateToBook={isTooLateToBook}
        numberOfSpotsLeft={numberOfSpotsLeft}
        service={service}
        isLoading={ctaLoadingServiceId === service.id}
        shouldNavigateImmediately={shouldNavigateImmediately}
      />
    </div>
  );
};

const OnlineBadge = ({ isOfferedOnline }: { isOfferedOnline?: Boolean }) => {
  const { shouldBeVisible } = useVisibilityCheck();
  const showBadge = isOfferedOnline && shouldBeVisible('isOnlineBadgeVisible');
  return showBadge ? <OnlineBadgeComponent /> : null;
};

const MoreInfoButton: FC<Pick<ServiceInfoProps, 'service'>> = ({ service }) => {
  const { shouldBeVisible } = useVisibilityCheck();
  const { onMoreInfoClick } = useWidgetActions();
  return shouldBeVisible('isMoreInfoButtonVisible') ? (
    <MoreInfoButtonComponent
      url={service.urls?.servicePage?.url!}
      onClick={() =>
        onMoreInfoClick({
          service,
          referralInfo: ReferralInfo.MORE_INFO_BUTTON,
        })
      }
    />
  ) : null;
};

const StaffMember = ({
  text,
  ellipsis,
}: {
  text: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  return (
    <Details
      type={ServiceDetailsType.STAFF_MEMBER}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        detailsClasses.staffMember,
        classes.staffMemberVisibility,
        'isStaffMemberVisible',
      )}
    >
      {text}
    </Details>
  );
};

const Divider = () => {
  const { shouldBeVisible } = useVisibilityCheck();
  const { addVisibilityClass } = useAddVisibilityClass();
  return shouldBeVisible('isServiceDividerVisible') ? (
    <TPADivider
      data-hook={DataHooks.DIVIDER}
      className={addVisibilityClass(
        classes.divider,
        classes.dividerVisibility,
        'isServiceDividerVisible',
      )}
    />
  ) : null;
};

const OfferedDays = ({
  offeredDays,
  ellipsis = false,
}: {
  offeredDays: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  return (
    <Details
      type={ServiceDetailsType.OFFERED_DAYS}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        '',
        classes.offeredDaysVisibility,
        'isServiceOfferedDaysVisible',
      )}
    >
      {offeredDays}
    </Details>
  );
};

const StartDate = ({
  startDate,
  ellipsis = false,
}: {
  startDate: string;
  locale?: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();

  return (
    <Details
      type={ServiceDetailsType.START_DATE}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        '',
        classes.startDateVisibility,
        'isServiceStartDateVisible',
      )}
    >
      {startDate}
    </Details>
  );
};

const Ended = ({ isEnded }: { isEnded: boolean }) => {
  const { t } = useTranslation();
  const { addVisibilityClass } = useAddVisibilityClass();
  if (!isEnded) {
    return null;
  }
  return (
    <Details
      className={addVisibilityClass(
        '',
        classes.startDateVisibility,
        'isServiceStartDateVisible',
      )}
    >
      {t('service.schedule.course.end.date.passed')}
    </Details>
  );
};

const Duration = ({
  duration,
  ellipsis = false,
}: {
  duration: string;
  ellipsis?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  return (
    <Details
      type={ServiceDetailsType.DURATION}
      ellipsis={ellipsis}
      className={addVisibilityClass(
        '',
        classes.durationVisibility,
        'isServiceDurationVisible',
      )}
    >
      {duration}
    </Details>
  );
};

const Price = ({
  displayedPrice,
  srOnlyPrice,
  priceAfterDiscount,
  discountName,
  isVariedPrice,
}: {
  displayedPrice: string;
  srOnlyPrice: string;
  priceAfterDiscount?: string;
  discountName?: string;
  isVariedPrice?: boolean;
}) => {
  const { addVisibilityClass } = useAddVisibilityClass();
  const showPriceAfterDiscount = !!priceAfterDiscount && !isVariedPrice;
  const hasDiscountInfo = showPriceAfterDiscount || !!discountName;

  return (
    <>
      <div
        data-hook={DataHooks.SR_ONLY_PRICE}
        className={addVisibilityClass(
          classes.srOnly,
          classes.priceVisibility,
          'isServicePriceVisible',
        )}
      >
        {srOnlyPrice}
      </div>
      {hasDiscountInfo ? (
        <div
          className={addVisibilityClass(
            classes.priceContainer,
            classes.priceVisibility,
            'isServicePriceVisible',
          )}
        >
          <div className={classes.pricesRow}>
            <Details
              type={ServiceDetailsType.PRICE}
              ariaHidden
              className={
                showPriceAfterDiscount
                  ? addVisibilityClass(
                      classes.originalPrice,
                      classes.priceAfterDiscountVisibility,
                      'isPriceAfterDiscountVisible',
                    )
                  : undefined
              }
            >
              {displayedPrice}
            </Details>
            {showPriceAfterDiscount && (
              <Details type={ServiceDetailsType.PRICE} ariaHidden>
                {priceAfterDiscount}
              </Details>
            )}
          </div>
          <DiscountName
            discountName={discountName}
            showTooltip={isVariedPrice || !priceAfterDiscount}
          />
        </div>
      ) : (
        <Details
          type={ServiceDetailsType.PRICE}
          ariaHidden
          className={addVisibilityClass(
            '',
            classes.priceVisibility,
            'isServicePriceVisible',
          )}
        >
          {displayedPrice}
        </Details>
      )}
    </>
  );
};

const BookButton: FC<BookButtonProps> = memo((props) => {
  const { shouldBeVisible } = useVisibilityCheck();

  return shouldBeVisible('isBookButtonVisible') ? (
    <BookButtonComponent {...props} />
  ) : null;
});

const ServiceActions = ({
  service,
  isTooLateToBook,
  numberOfSpotsLeft,
  isLoading,
  shouldNavigateImmediately,
}: {
  isTooLateToBook?: boolean;
  numberOfSpotsLeft?: number;
  service: EnrichedService;
  isLoading: boolean;
  shouldNavigateImmediately: boolean;
}) => {
  const { onActionButtonClick, setCtaLoadingService } = useWidgetActions();
  const { addVisibilityClass } = useAddVisibilityClass();
  const { shouldBeVisible } = useVisibilityCheck();
  const settings = useSettings();
  const { experiments } = useExperiments();
  const destination = getBookButtonClickDestination({
    service,
    experiments,
    settings,
    numberOfSpotsLeft,
  });
  // A course that routes to the Booking Form is still joinable, so its button
  // should invite booking ("Book Now") rather than the "View Course" fallback
  // shown when the click can only lead to the Service Page.
  const showViewCourse =
    destination !== ClickDestination.BOOKING_FORM &&
    (isTooLateToBook || numberOfSpotsLeft! <= 0);
  const onBookButtonClick = useCallback(() => {
    if (shouldNavigateImmediately) {
      setCtaLoadingService(service.id!);
    }

    void onActionButtonClick({
      service,
      referralInfo: ReferralInfo.BOOK_BUTTON,
    });
  }, [service]);

  const servicePageUrl = service.urls?.servicePage?.url!;
  const calendarPageUrl = service.urls?.calendarPage?.url!;
  // The booking form has no static URL, so its href is suppressed and the click
  // handler drives navigation.
  const bookButtonUrl =
    !shouldNavigateImmediately || destination === ClickDestination.BOOKING_FORM
      ? undefined
      : destination === ClickDestination.SERVICE_PAGE
      ? servicePageUrl
      : calendarPageUrl;

  return shouldBeVisible('isBookButtonVisible') ? (
    <div
      className={addVisibilityClass(
        classes.button,
        classes.buttonVisibility,
        'isBookButtonVisible',
      )}
    >
      <BookButton
        onClick={onBookButtonClick}
        url={bookButtonUrl}
        loading={isLoading}
        isNoBookFlow={service.displayOnlyNoBookFlow}
        isPendingApprovalFlow={service.onlineBooking?.requireManualApproval!}
        showViewCourse={showViewCourse}
        serviceId={service.id!}
      />
      <ExplorePlans service={service} />
    </div>
  ) : (
    <ExplorePlans service={service} onlyLink />
  );
};

const useTitleTabIndex = (
  service: EnrichedService,
  numberOfSpotsLeft?: number,
) => {
  const settings = useSettings();
  const { experiments } = useExperiments();
  const { shouldBeVisible } = useVisibilityCheck();

  const destination = getServiceNameClickDestination({
    service,
    experiments,
    settings,
    numberOfSpotsLeft,
  });

  return destination === ClickDestination.SERVICE_PAGE
    ? shouldBeVisible('isMoreInfoButtonVisible')
      ? -1
      : 0
    : shouldBeVisible('isBookButtonVisible')
    ? -1
    : 0;
};
